import React, { useCallback, useRef, useState } from 'react';
import { useBool } from '#lib/hooks/use-boolean-state.js';
import { cn } from '#lib/helpers/class.js';
import { Icon } from '@iconify-icon/react';
import { filterUselessFields, sameIdOrImportId } from '#@itinerary/places/helpers.js';
import { checkIfDiffExist } from './helpers.js';
import { convertsToPlainText, createIntlTextForEditFields } from '#helpers/helpers.js';

export function DataDiff(props) {
    const {
        incomingData,
        currentData,
        updatedData,
        onUpdateData,
        onRestoreData,
        onReplaceSuggestionPlan,
        onRestoreSuggestionPlan,
        isPlan,
        places
    } = props;

    const [isIncomingHovering, onMouseOverIncoming, onMouseOutIncoming] = useBool(false);
    const [isCurrentHovering, onMouseOverCurrent, onMouseOutCurrent] = useBool(false);
    const [hoveredKey, setHoveredKey] = useState('');

    const suggestionDiff =
        incomingData?.type === 'suggestion'
            ? checkIfDiffExist(convertsToPlainText(incomingData), convertsToPlainText(currentData))
            : null;

    if (incomingData.type === 'suggestion' && suggestionDiff) {
        return (
            <div className="w-full relative grid grid-cols-12 border border-neutral-200 rounded-md bg-neutral-50">
                <div className="grid grid-cols-12 col-span-full bg-neutral-200 rounded-t-md divide-x divide-neutral-300">
                    <p className="font-semibold text-sm text-center col-span-4 p-2">
                        {_t.edit.updateItinerary.incoming()}
                    </p>
                    <p className="font-semibold text-sm text-center col-span-4 p-2">
                        {_t.edit.updateItinerary.current()}
                    </p>
                    <p className="font-semibold text-sm text-center col-span-4 p-2 border-l border-neutral-500">
                        {_t.edit.updateItinerary.updated()}
                    </p>
                </div>
                <div className="grid grid-cols-12 col-span-full rounded-b-md divide-x divide-neutral-300">
                    <div className="relative h-full col-span-4 grid grid-rows-4">
                        <div
                            className={cn(
                                'absolute left-0 top-0 w-full h-full bg-primary-100 transition-all duration-200 opacity-0 cursor-pointer hover:opacity-100 opacity-0 hover:opacity-100'
                            )}
                            onMouseOver={onMouseOverIncoming}
                            onMouseOut={onMouseOutIncoming}
                            onClick={onReplaceSuggestionPlan}
                        >
                            <Icon
                                icon="fluent:arrow-right-12-filled"
                                width="1.4rem"
                                height="1.4rem"
                                className={cn(
                                    'absolute left-1/2 top-1/2 -translate-y-1/2 text-primary-500 transition-all duration-200',
                                    isIncomingHovering ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-1/2'
                                )}
                            />
                        </div>
                        {Object.keys(filterUselessFields(incomingData)).map((key) => {
                            return (
                                <div
                                    className={cn(
                                        'row-span-1 p-1',
                                        incomingData[key] !== currentData[key]
                                            ? incomingData[key] === updatedData[key]
                                                ? 'bg-primary-100 text-primary-500'
                                                : 'bg-red-100 text-red-600'
                                            : ''
                                    )}
                                >
                                    <span className="font-semibold">{createIntlTextForEditFields(key)}</span>
                                    <p className="pl-[2px] text-sm">
                                        {key === 'name'
                                            ? places.find((place) => sameIdOrImportId(place, incomingData.suggestionId))
                                                  ?.name
                                            : convertsToPlainText(incomingData[key]) || _t.global.emptyString()}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                    <div className="relative h-full col-span-4 grid grid-rows-4">
                        <div
                            className={cn(
                                'absolute left-0 top-0 w-full h-full bg-primary-100 transition-all duration-200 opacity-0 cursor-pointer hover:opacity-100'
                            )}
                            onMouseOver={onMouseOverCurrent}
                            onMouseOut={onMouseOverCurrent}
                            onClick={onRestoreSuggestionPlan}
                        >
                            <Icon
                                icon="fluent:arrow-right-12-filled"
                                width="1.4rem"
                                height="1.4rem"
                                className={cn(
                                    'absolute left-1/2 top-1/2 -translate-y-1/2 text-primary-500 transition-all duration-200',
                                    isCurrentHovering ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-1/2'
                                )}
                            />
                        </div>
                        {Object.keys(filterUselessFields(currentData)).map((key) => {
                            return (
                                <div
                                    className={cn(
                                        'row-span-1 p-1',
                                        incomingData[key] !== currentData[key]
                                            ? updatedData[key] === currentData[key]
                                                ? 'bg-primary-100 text-primary-500'
                                                : 'bg-red-100 text-red-600'
                                            : ''
                                    )}
                                >
                                    <span className="font-semibold">{createIntlTextForEditFields(key)}</span>
                                    <p className="pl-[2px] text-sm">
                                        {key === 'name'
                                            ? places.find((place) => sameIdOrImportId(place, currentData.suggestionId))
                                                  ?.name
                                            : convertsToPlainText(currentData[key]) || _t.global.emptyString()}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                    <div className="relative h-full col-span-4 grid grid-rows-4">
                        {Object.keys(filterUselessFields(updatedData)).map((key) => {
                            return (
                                <div
                                    className={cn(
                                        'row-span-1 p-1',
                                        incomingData[key] !== currentData[key] && 'bg-primary-100 text-primary-500'
                                    )}
                                >
                                    <span className="font-semibold">{createIntlTextForEditFields(key)}</span>
                                    <p className="pl-[2px] text-sm">
                                        {key === 'name'
                                            ? places.find((place) => sameIdOrImportId(place, updatedData.suggestionId))
                                                  ?.name
                                            : convertsToPlainText(updatedData[key]) || _t.global.emptyString()}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {Object.keys(filterUselessFields(incomingData)).map((key) => {
                const valueDiff = convertsToPlainText(incomingData[key]) !== convertsToPlainText(currentData[key]);
                const updatedValue = convertsToPlainText(incomingData[key]) === convertsToPlainText(updatedData[key]);
                if (isPlan && !valueDiff) return null;

                // vertical diff (too long values)
                if (incomingData[key]?.length > 100 || currentData[key]?.length > 100) {
                    if (!valueDiff) return null;
                    return (
                        <div key={key} className="w-full flex flex-col gap-1">
                            <p className="font-semibold pl-1">{createIntlTextForEditFields(key)}</p>
                            <div className="w-full flex flex-col divide-y divide-neutral-300 border border-neutral-200 rounded-md bg-neutral-50">
                                <div className="w-full flex text-wrap h-[50%]">
                                    <p className="bg-neutral-200 font-semibold text-sm text-center w-1/4 p-2">
                                        {_t.edit.updateItinerary.incoming()}
                                    </p>

                                    <p
                                        className={cn(
                                            'relative transition-all text-sm text-center w-3/4 p-2 no-whitespace text-ellipsis h-full overflow-auto max-h-[300px]',
                                            updatedValue && valueDiff
                                                ? 'bg-primary-50 text-primary-500 rounded-bl-md cursor-default'
                                                : 'cursor-pointer hover:bg-primary-50 hover:text-primary-500'
                                        )}
                                        onClick={() => {
                                            onUpdateData(key);
                                        }}
                                        onMouseOver={() => {
                                            setHoveredKey(key);
                                            onMouseOverIncoming();
                                        }}
                                        onMouseOut={() => {
                                            onMouseOutIncoming();
                                        }}
                                    >
                                        <span className="w-full overflow-auto max-h-[300px]">
                                            {convertsToPlainText(incomingData[key]) || _t.global.emptyString()}
                                        </span>
                                    </p>
                                </div>

                                <div className="w-full flex text-wrap">
                                    <p className="bg-neutral-200 font-semibold text-sm text-center w-1/4 p-2">
                                        {_t.edit.updateItinerary.current()}
                                    </p>
                                    <p
                                        className={cn(
                                            'relative transition-all text-sm text-center h-full w-3/4 p-2 no-whitespace text-ellipsis overflow-auto max-h-[300px]',
                                            !updatedValue && valueDiff
                                                ? 'bg-primary-50 text-primary-500 rounded-br-md cursor-default'
                                                : 'cursor-pointer hover:bg-primary-50 hover:text-primary-500'
                                        )}
                                        onClick={() => onRestoreData(key)}
                                        onMouseOver={() => {
                                            setHoveredKey(key);
                                            onMouseOverCurrent();
                                        }}
                                        onMouseOut={() => {
                                            onMouseOutCurrent();
                                        }}
                                    >
                                        <span className="w-full overflow-auto max-h-[300px]">
                                            {convertsToPlainText(currentData[key]) || _t.global.emptyString()}
                                        </span>
                                    </p>
                                </div>

                                <div className="w-full flex text-wrap">
                                    <p className="bg-neutral-200 font-semibold text-sm text-center w-1/4 p-2 border-neutral-500">
                                        {_t.edit.updateItinerary.updated()}
                                    </p>
                                    <p className="transition-all border-neutral-200 text-sm text-center font-semibold w-3/4 h-full p-2 no-whitespace text-ellipsis overflow-auto max-h-[300px]">
                                        {convertsToPlainText(updatedData[key]) || _t.global.emptyString()}
                                    </p>
                                </div>
                            </div>
                        </div>
                    );
                }

                if (typeof incomingData[key] !== 'object' && key !== 'id' && key !== 'importId' && key !== 'type') {
                    if (!valueDiff) return null;
                    return (
                        <div key={key} className="w-full flex flex-col gap-1 h-full">
                            <p className="font-semibold pl-1">{createIntlTextForEditFields(key)}</p>
                            <div className="w-full relative grid grid-cols-12 border border-neutral-200 rounded-md bg-neutral-50">
                                <div className="grid grid-cols-12 col-span-full bg-neutral-200 rounded-t-md divide-x divide-neutral-300">
                                    <p className="font-semibold text-sm text-center col-span-4 p-2">
                                        {_t.edit.updateItinerary.incoming()}
                                    </p>
                                    <p className="font-semibold text-sm text-center col-span-4 p-2 border-l border-neutral-500">
                                        {_t.edit.updateItinerary.current()}
                                    </p>
                                    <p className="font-semibold text-sm text-center col-span-4 p-2">
                                        {_t.edit.updateItinerary.updated()}
                                    </p>
                                </div>
                                <p
                                    className={cn(
                                        'relative transition-all text-sm text-center no-whitespace text-ellipsis overflow-hidden col-span-4 max-h-32 p-2',
                                        updatedValue && valueDiff
                                            ? 'bg-primary-50 text-primary-500 rounded-bl-md cursor-default'
                                            : 'cursor-pointer hover:bg-primary-50 hover:text-primary-500'
                                    )}
                                    onClick={() => {
                                        onUpdateData(key);
                                    }}
                                    onMouseOver={() => {
                                        setHoveredKey(key);
                                        onMouseOverIncoming();
                                    }}
                                    onMouseOut={() => {
                                        onMouseOutIncoming();
                                    }}
                                >
                                    <span>
                                        {incomingData.type === 'suggestion' && key === 'name'
                                            ? places.find((place) => sameIdOrImportId(place, incomingData.suggestionId))
                                                  ?.name
                                            : incomingData[key] || _t.global.emptyString()}
                                    </span>
                                </p>
                                <p
                                    className={cn(
                                        'border-x border-neutral-200 relative transition-all text-sm text-center no-whitespace text-ellipsis overflow-hidden col-span-4 max-h-32 p-2',
                                        !updatedValue && valueDiff
                                            ? 'bg-primary-50 text-primary-500 rounded-br-md cursor-default'
                                            : 'cursor-pointer hover:bg-primary-50 hover:text-primary-500'
                                    )}
                                    onClick={() => onRestoreData(key)}
                                    onMouseOver={() => {
                                        setHoveredKey(key);
                                        onMouseOverCurrent();
                                    }}
                                    onMouseOut={() => {
                                        onMouseOutCurrent();
                                    }}
                                >
                                    <span>
                                        {currentData.type === 'suggestion' && key === 'name'
                                            ? places.find((place) => sameIdOrImportId(place, currentData.suggestionId))
                                                  ?.name
                                            : currentData[key] || _t.global.emptyString()}
                                    </span>
                                </p>
                                <p className="transition-all text-sm text-center font-semibold no-whitespace text-ellipsis overflow-hidden col-span-4 max-h-32 p-2">
                                    {updatedData[key] || _t.global.emptyString()}
                                </p>
                            </div>
                        </div>
                    );
                }
            })}
        </>
    );
}
